import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { jwtClaim } from '../../util-helpers';
import {
  InspectionWizardVMBaseClass,
  RCIPInspectionRequestVM,
  RCIPInspectionWizardVM,
} from '../models/coverage-validator-api-models';
import { AuthStore } from '../auth/auth.store';
import { InspectionService } from './inspection.service';

// One member per product. The generated base-class types are unusable: their productId is both number and "1", so they reduce to never.
export type ProductRequest = RCIPInspectionRequestVM;
export type ProductWizard = RCIPInspectionWizardVM;

type InspectionStatus = 'loading' | 'ready' | 'completed' | 'signedOut' | 'notFound' | 'closed' | 'error';

interface InspectionState {
  request: ProductRequest | null;
  // The last wizard the API accepted; null until the insured's first save.
  wizard: ProductWizard | null;
  status: InspectionStatus;
  saveError: string | null;
}

const initialState: InspectionState = {
  request: null,
  wizard: null,
  status: 'loading',
  saveError: null,
};

function httpStatus(e: unknown): number {
  return e instanceof HttpErrorResponse ? e.status : 0;
}

export const InspectionStore = signalStore(
  { providedIn: 'root' },
  withState<InspectionState>(initialState),
  withComputed((_, authStore = inject(AuthStore)) => ({
    // Insured tokens are scoped to one inspection: CoverageValidatorAPI's TokenService.InspectionIdClaimType.
    id: computed(() => jwtClaim(authStore.accessToken(), 'InspectionId')),
  })),
  withMethods((store, inspectionService = inject(InspectionService)) => ({
    async load() {
      patchState(store, initialState);
      if (!store.id()) {
        patchState(store, { status: 'signedOut' });
        return;
      }
      try {
        const response = await inspectionService.getInspection();
        patchState(store, {
          request: (response.request as ProductRequest | null) ?? null,
          wizard: (response.wizard as ProductWizard | null) ?? null,
          status: 'ready',
        });
      } catch (e) {
        const status = httpStatus(e);
        patchState(store, {
          status: status === 401 ? 'signedOut' : status === 404 ? 'notFound' : 'error',
        });
      }
    },

    // Overwrites the whole wizard. Resolves false when the API refused it, so the step stays put.
    async save(wizard: ProductWizard): Promise<boolean> {
      if (!store.id()) return false;

      patchState(store, { saveError: null });
      try {
        await inspectionService.saveWizard(wizard as InspectionWizardVMBaseClass);
        patchState(store, { wizard });
        return true;
      } catch (e) {
        const status = httpStatus(e);
        if (status === 410 || status === 401) {
          patchState(store, { status: status === 410 ? 'closed' : 'signedOut' });
        } else {
          patchState(store, {
                      saveError: "We couldn't save your answers. Check your connection and try again.",
          });
        }
        return false;
      }
    },

    // The insured accepts everything on file; the inspection can't change after this.
    async complete(): Promise<void> {
      if (!store.id()) return;

      patchState(store, { saveError: null });
      try {
        await inspectionService.completeInspection();
        patchState(store, { status: 'completed' });
      } catch (e) {
        const status = httpStatus(e);
        if (status === 410 || status === 401) {
          patchState(store, { status: status === 410 ? 'closed' : 'signedOut' });
        } else {
          patchState(store, {
                      saveError: "We couldn't submit your inspection. Check your connection and try again.",
          });
        }
      }
    },
  })),
);
